function setCookie(name, value, days) {
  document.cookie = `${name}=${encodeURIComponent(value)};max-age=${days * 86400};path=/;SameSite=Lax`;
}
function getCookie(name) {
  const cookies = document.cookie.split(";");
  const foundCookie = cookies.find((cookie) => {
    const [cookieName] = cookie.split("=");
    return cookieName.trim() === name;
  });
  const value = foundCookie?.split("=")[1];
  return value ? decodeURIComponent(value) : value;
}
const COOKIE_NAME = "geolocation";
const COOKIE_DAYS = 30;
const URL_PARAM = "geolocation";
class Geolocator extends HTMLElement {
  geolocatorScript;
  countryDataArray;
  geolocatorContentTemplate;
  geolocatorContentPlaceholder;
  modal;
  closeModalButton;
  stayButton;
  leaveButton;
  overlay;
  recommendedMatch;
  isOpen = false;
  async connectedCallback() {
    this.initDataAndElements();
    if (!this.modal || !this.geolocatorContentTemplate || !this.countryDataArray?.length) return;
    const mode = this.dataset.mode || "enabled";
    const isDesignMode = this.dataset.designMode === "true";
    const isTestMode = mode === "test";
    if (mode === "disabled") return;
    if (!isTestMode) {
      const handledParam = this.handleGeolocationParam();
      if (handledParam) return;
      if (getCookie(COOKIE_NAME)) return;
      if (isDesignMode) return;
    }
    const browsingContext = await this.getBrowsingContext();
    const currentMarket = this.dataset.currentMarket;
    let recommendedMatch = this.getRecommendedMatch(browsingContext);
    if (!recommendedMatch || recommendedMatch.market === currentMarket) {
      if (!isTestMode) return;
      recommendedMatch = this.countryDataArray.find((block) => block.market !== currentMarket) || this.countryDataArray.find((block) => block.primary) || this.countryDataArray[0];
    }
    this.recommendedMatch = recommendedMatch;
    this.setupGeolocatorContent(recommendedMatch);
    this.openGeolocator();
  }
  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
  }
  /**
   * Reads the ?geolocation=XX handoff param set by the origin domain, converts it
   * into this domain's cookie and strips it from the URL.
   *
   * @returns `true` when the param was present and handled.
   */
  handleGeolocationParam() {
    const url = new URL(window.location.href);
    const geolocation = url.searchParams.get(URL_PARAM);
    if (!geolocation) return false;
    setCookie(COOKIE_NAME, geolocation, COOKIE_DAYS);
    url.searchParams.delete(URL_PARAM);
    window.history.replaceState(null, "", url.toString());
    return true;
  }
  openGeolocator() {
    this.isOpen = true;
    this.classList.remove("tw:hidden");
    this.setAttribute("geolocation-modal-active", "true");
    document.addEventListener("keydown", this.handleKeydown);
    setTimeout(() => {
      this.modal.classList.add("active");
      this.leaveButton?.focus();
    }, 300);
  }
  closeGeolocator() {
    this.isOpen = false;
    this.modal.classList.remove("active");
    this.setAttribute("geolocation-modal-active", "false");
    document.removeEventListener("keydown", this.handleKeydown);
    setTimeout(() => {
      this.classList.add("tw:hidden");
    }, 300);
  }
  /**
   * Any dismissal (stay button, close button, overlay, Escape) counts as "stay":
   * set the cookie so the visitor is never nagged again on this domain.
   */
  stayHere = () => {
    const currentCountry = this.dataset.currentCountry || window.Shopify?.country || "stay";
    setCookie(COOKIE_NAME, currentCountry, COOKIE_DAYS);
    this.closeGeolocator();
  };
  handleKeydown = (event) => {
    if (event.key === "Escape" && this.isOpen) {
      this.stayHere();
    }
  };
  setupGeolocatorContent(recommendedMatch) {
    const geolocatorContent = this.geolocatorContentTemplate.content.cloneNode(true);
    const content = geolocatorContent.querySelector(`[data-id="${recommendedMatch.id}"]`);
    if (content) {
      this.geolocatorContentPlaceholder.appendChild(content);
    }
    this.stayButton = this.querySelector(".stay-button");
    this.leaveButton = this.querySelector(".leave-button");
    this.stayButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.stayHere();
    });
    this.leaveButton?.addEventListener("click", (event) => {
      event.preventDefault();
      const { locale, handle, type } = this.leaveButton.dataset;
      if (!handle) return;
      setCookie(COOKIE_NAME, handle, COOKIE_DAYS);
      if (type !== "market") {
        const href = this.leaveButton.getAttribute("href");
        const target = new URL(href || window.location.href, window.location.origin);
        target.searchParams.set(URL_PARAM, handle);
        window.location.href = target.toString();
        return;
      }
      this.handleMarketSwitch(handle, locale || "");
    });
    this.closeModalButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.stayHere();
    });
    this.overlay?.addEventListener("click", () => {
      this.stayHere();
    });
  }
  initDataAndElements() {
    this.modal = this.querySelector("#geolocator-modal");
    this.closeModalButton = this.querySelector("#close-geolocator-modal");
    this.geolocatorScript = this.querySelector("#geolocator-script");
    this.geolocatorContentTemplate = this.querySelector("#geolocator-content-template");
    this.geolocatorContentPlaceholder = this.querySelector("#geolocator-content-placeholder");
    this.overlay = this.querySelector(".geolocator-overlay");
    try {
      this.countryDataArray = this.geolocatorScript ? JSON.parse(this.geolocatorScript.innerHTML) : [];
    } catch {
      this.countryDataArray = [];
    }
  }
  /**
   * Switches market via Shopify's localization endpoint. Shopify redirects to the
   * target market's domain and carries the relative `return_to` (same page) along,
   * including the ?geolocation param the destination turns into its own cookie.
   */
  handleMarketSwitch(handle, locale) {
    const returnTo = new URL(window.location.href);
    returnTo.searchParams.set(URL_PARAM, handle);
    const formElement = document.createElement("form");
    formElement.action = "/localization";
    formElement.method = "POST";
    formElement.hidden = true;
    const fields = {
      _method: "PUT",
      country_code: handle,
      language_code: locale,
      return_to: returnTo.pathname + returnTo.search
    };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      formElement.appendChild(input);
    });
    document.body.appendChild(formElement);
    formElement.submit();
  }
  getRecommendedMatch(browsingContext) {
    if (!browsingContext) return void 0;
    const { suggestedCountry, suggestedLanguage, detectedCountry } = browsingContext;
    const countryHandle = suggestedCountry?.handle || detectedCountry?.handle;
    const languageHandle = suggestedLanguage?.handle;
    if (!countryHandle) return void 0;
    let recommendedMatch = this.countryDataArray.find((block) => block.handle === countryHandle && block.locale === languageHandle);
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((block) => block.handle === countryHandle);
    }
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((block) => block.primary);
    }
    return recommendedMatch;
  }
  async getBrowsingContext() {
    try {
      const response = await fetch("/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true");
      if (!response.ok) return null;
      const data = await response.json();
      const detectedValues = data.detected_values;
      const { handle, name } = detectedValues.country;
      const { suggestions } = data;
      let suggestedCountry = null;
      let suggestedLanguage = null;
      if (suggestions?.length) {
        const suggestion = suggestions.find((_suggestion) => _suggestion.parts.country.handle === detectedValues.country.handle);
        suggestedCountry = suggestion?.parts.country;
        suggestedLanguage = suggestion?.parts.language;
      }
      return {
        detectedCountry: {
          handle,
          name
        },
        suggestedCountry,
        suggestedLanguage
      };
    } catch {
      return null;
    }
  }
}
if (!customElements.get("geolocator-component")) {
  customElements.define("geolocator-component", Geolocator);
}
